import React from 'react';
import './LandingPage.css';
import { Typography, Row, Col, Card, Button, Space, Tag, Divider, Statistic } from 'antd';
import { TruckOutlined, GiftOutlined, HeartOutlined, ClockCircleOutlined, PhoneOutlined, MessageOutlined, StarOutlined, CheckCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Breadcrumbs, getPageBreadcrumbs } from '../../shared/ui/Breadcrumbs';
import { useSEO } from '../../shared/ui/SEOHead';
import { SHOP_INFO } from '../../shared/constants/shopData';

const { Title, Text, Paragraph } = Typography;

interface LandingPageProps {
    title: string;
    subtitle: string;
    heroText: string;
    h1: string;
    keywords: string[];
    advantages: Array<{icon: React.ReactNode; title: string; desc: string}>;
    services: string[];
    faq: Array<{q: string; a: string}>;
    ctaText: string;
    ctaButtons: Array<{label: string; href: string; primary?: boolean; icon?: React.ReactNode}>;
    schemaType: 'Product' | 'Service';
    schemaName: string;
    schemaDescription: string;
    schemaAreaServed: string[];
    schemaPriceRange?: string;
    breadcrumbItems?: Array<{name: string; url: string}>;
    pagePath?: string;
}

export const LandingPage: React.FC<LandingPageProps> = ({
    title,
    subtitle,
    heroText,
    h1,
    keywords,
    advantages,
    services,
    faq,
    ctaText,
    ctaButtons,
    schemaType,
    schemaName,
    schemaDescription,
    schemaAreaServed,
    schemaPriceRange,
    breadcrumbItems,
    pagePath
}) => {
    // Dynamic Meta Tags (Open Graph, Twitter Cards, Canonical)
    useSEO({
        title,
        description: schemaDescription || heroText,
        keywords,
        canonicalUrl: pagePath ? `https://vanilufa.ru${pagePath}` : undefined
    });

    // JSON-LD structured data
    const schema = {
        "@context": "https://schema.org",
        "@type": schemaType,
        "name": schemaName,
        "description": schemaDescription,
        "areaServed": schemaAreaServed.map(name => ({ "@type": "Place", name })),
        "provider": {
            "@type": "LocalBusiness",
            "name": "Цветочный магазин «Ваниль»",
            "telephone": "+7-987-258-81-11",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "ул. Дуговая, 3/3",
                "addressLocality": "Булгаково",
                "addressRegion": "Республика Башкортостан",
                "addressCountry": "RU"
            },
            "url": "https://vanilufa.ru/"
        },
        ...(schemaPriceRange && { "offers": { "@type": "Offer", "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "RUB" } } })
    };

    // BreadcrumbList schema
    const breadcrumbSchema = breadcrumbItems ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
        }))
    } : null;

    // UI breadcrumbs
    const uiBreadcrumbs = pagePath ? getPageBreadcrumbs(pagePath) : (breadcrumbItems 
        ? breadcrumbItems.map(item => ({ name: item.name, url: item.url }))
        : [{ name: 'Главная', url: '/' }]);

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
            {breadcrumbSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
                />
            )}
            <div className="landing-page">
                <Breadcrumbs items={uiBreadcrumbs} className="landing-page__breadcrumbs" />
                <section className="landing-page__hero">
                    <div className="landing-page__hero-content">
                        <Tag color="pink" className="landing-page__badge">
                            <GiftOutlined /> {subtitle}
                        </Tag>
                        <Title level={1} className="landing-page__h1">{h1}</Title>
                        <Text type="secondary" className="landing-page__hero-text">{heroText}</Text>
                        <Space wrap size="middle" className="landing-page__hero-cta">
                            {ctaButtons.map((btn, i) => (
                                <a
                                    key={i}
                                    href={btn.href}
                                    target={btn.href.startsWith('http') ? '_blank' : undefined}
                                    rel={btn.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                    className={`landing-page__btn ${btn.primary ? 'landing-page__btn--primary' : 'landing-page__btn--secondary'}`}
                                >
                                    {btn.icon} {btn.label}
                                </a>
                            ))}
                        </Space>
                        <div className="landing-page__trust">
                            <Space wrap className="landing-page__trust-items">
                                <Space><CheckCircleOutlined style={{color: '#52c41a'}} /> Бесплатная доставка от 3000₽*</Space>
                                <Space><CheckCircleOutlined style={{color: '#52c41a'}} /> Сборка по фото за 2 часа</Space>
                                <Space><CheckCircleOutlined style={{color: '#52c41a'}} /> Гарантия свежести 24 часа</Space>
                                <Space><CheckCircleOutlined style={{color: '#52c41a'}} /> Ежедневная доставка</Space>
                            </Space>
                            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                * кроме праздничных и выходных дней
                            </div>
                        </div>
                    </div>
                </section>

                <Divider className="landing-page__divider" />

                <section className="landing-page__section landing-page__advantages">
                    <div className="landing-page__container">
                        <Title level={2} className="landing-page__section-title">Почему заказывают у нас</Title>
                        <Row gutter={[24, 24]} className="landing-page__advantages-grid">
                            {advantages.map((adv, i) => (
                                <Col key={i} xs={24} md={12} lg={8}>
                                    <Card className="landing-page__advantage-card" variant="borderless">
                                        <Space orientation="vertical" size="middle" style={{ textAlign: 'center' }}>
                                            <span className="landing-page__adv-icon">{adv.icon}</span>
                                            <Title level={4}>{adv.title}</Title>
                                            <Text type="secondary">{adv.desc}</Text>
                                        </Space>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </section>

                <Divider className="landing-page__divider" />

                <section className="landing-page__section landing-page__services">
                    <div className="landing-page__container">
                        <Title level={2} className="landing-page__section-title">Что входит в услугу</Title>
                        <Row gutter={[16, 16]} className="landing-page__services-grid">
                            {services.map((svc, i) => (
                                <Col key={i} xs={24} sm={12} md={8}>
                                    <Card className="landing-page__service-card" variant="borderless">
                                        <Space>
                                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16, flexShrink: 0 }} />
                                            <Text style={{ flex: 1 }}>{svc}</Text>
                                        </Space>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </section>

                <Divider className="landing-page__divider" />

                <section className="landing-page__section landing-page__faq">
                    <div className="landing-page__container">
                        <Title level={2} className="landing-page__section-title">Частые вопросы</Title>
                        <div className="landing-page__faq-list">
                            {faq.map((item, index) => (
                                <div
                                    key={index}
                                    className="landing-page__faq-item"
                                    style={{
                                        padding: '16px 0',
                                        borderBottom: index === faq.length - 1 ? 'none' : '1px solid #f0f0f0'
                                    }}
                                >
                                    <Space orientation="vertical" size={8} style={{ width: '100%' }}>
                                        <Text strong style={{ fontSize: 16, color: '#1a1a1a' }}>{item.q}</Text>
                                        <Text type="secondary" style={{ lineHeight: 1.7 }}>{item.a}</Text>
                                    </Space>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <Divider className="landing-page__divider" />

                <section className="landing-page__section landing-page__cta-section">
                    <div className="landing-page__container">
                        <Card className="landing-page__cta-card" variant="borderless">
                            <Space orientation="vertical" size="large" style={{ textAlign: 'center', width: '100%' }}>
                                <Title level={2}>{ctaText}</Title>
                                <Space wrap size="middle" style={{ justifyContent: 'center' }}>
                                    {ctaButtons.map((btn, i) => (
                                        <a
                                            key={i}
                                            href={btn.href}
                                            target={btn.href.startsWith('http') ? '_blank' : undefined}
                                            rel={btn.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                            className={`landing-page__btn ${btn.primary ? 'landing-page__btn--primary' : 'landing-page__btn--secondary'}`}
                                        >
                                            {btn.icon} {btn.label}
                                        </a>
                                    ))}
                                </Space>
                            </Space>
                        </Card>
                    </div>
                </section>

                <section className="landing-page__section landing-page__addresses">
                    <div className="landing-page__container">
                        <Title level={2} className="landing-page__section-title">Где нас найти</Title>
                        <Row gutter={[24, 24]}>
                            <Col xs={24} md={12}>
                                <Card className="landing-page__address-card" variant="borderless">
                                    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                                        <Space>
                                            <EnvironmentOutlined style={{ color: '#e91e63', fontSize: 20 }} />
                                            <Title level={4} style={{ margin: 0 }}>Булгаково</Title>
                                        </Space>
                                        <Text strong>{SHOP_INFO.addresses.bulgakovo}</Text>
                                        <Space>
                                            <ClockCircleOutlined />
                                            <Text>Ежедневно: {SHOP_INFO.workingHours.bulgakovo}</Text>
                                        </Space>
                                        <Space wrap style={{ marginTop: 8 }}>
                                            <a
                                                href={`tel:${SHOP_INFO.phoneRaw}`}
                                                className="landing-page__card-btn landing-page__card-btn--call"
                                            >
                                                <PhoneOutlined /> Позвонить
                                            </a>
                                            <a
                                                href={`https://yandex.ru/maps/?text=${encodeURIComponent(SHOP_INFO.addresses.bulgakovo)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="landing-page__card-btn landing-page__card-btn--map"
                                            >
                                                <EnvironmentOutlined /> Точка на карте
                                            </a>
                                        </Space>
                                    </Space>
                                </Card>
                            </Col>
                            <Col xs={24} md={12}>
                                <Card className="landing-page__address-card" variant="borderless">
                                    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                                        <Space>
                                            <EnvironmentOutlined style={{ color: '#e91e63', fontSize: 20 }} />
                                            <Title level={4} style={{ margin: 0 }}>Уфа</Title>
                                        </Space>
                                        <Text strong>{SHOP_INFO.addresses.ufa}</Text>
                                        <Space>
                                            <ClockCircleOutlined />
                                            <Text>Ежедневно: {SHOP_INFO.workingHours.ufa}</Text>
                                        </Space>
                                        <Space wrap style={{ marginTop: 8 }}>
                                            <a
                                                href={`tel:${SHOP_INFO.phoneRaw}`}
                                                className="landing-page__card-btn landing-page__card-btn--call"
                                            >
                                                <PhoneOutlined /> Позвонить
                                            </a>
                                            <a
                                                href={`https://yandex.ru/maps/?text=${encodeURIComponent(SHOP_INFO.addresses.ufa)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="landing-page__card-btn landing-page__card-btn--map"
                                            >
                                                <EnvironmentOutlined /> Точка на карте
                                            </a>
                                        </Space>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </section>
            </div>
        </>
    );
};